import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type { EntityActivityRow, CommunicationEntityType } from "@adva/types";

interface ListResponse {
  activity: EntityActivityRow[];
  total: number;
}

// Two mutually-exclusive modes that map onto the /core/entity-activity route's
// XOR query contract: (entity_type + entity_id) vs opportunity_id (see
// apps/api/src/modules/core/entity-activity.routes.ts).
export type EntityActivityFilters =
  | {
      entityType: CommunicationEntityType;
      entityId: string;
      opportunityId?: never;
      limit?: number;
    }
  | {
      entityType?: never;
      entityId?: never;
      opportunityId: string;
      limit?: number;
    };

/**
 * Reads /core/entity-activity — the three-source merged timeline
 * (communications + media submissions + allowlisted activity events),
 * interleaved newest-first. Replaces the communications-only `useCommunications`
 * on the entity-activity surfaces so linked `activity_events` and job-site
 * **media/photos** reach the timeline (ADV-1115 built the endpoint; the feed +
 * preview never called it).
 *
 * Two mutually-exclusive modes mirror the API and `useCommunications`:
 *   - per-entity feed     → entityType + entityId
 *   - opportunity rollup  → opportunityId  (server merges comms across the
 *                           opportunity AND its child jobs / proposals /
 *                           invoices, plus that opportunity's media + events)
 *
 * Attachments are always hydrated by the service — there is no `include` param
 * on this route (unlike /core/communications).
 */
export function useEntityActivity(filters: EntityActivityFilters | null) {
  const ready =
    filters != null &&
    ("opportunityId" in filters ? !!filters.opportunityId : !!filters.entityId);

  const qs = new URLSearchParams();
  if (filters && "opportunityId" in filters && filters.opportunityId) {
    qs.set("opportunity_id", filters.opportunityId);
  } else if (filters && filters.entityType && filters.entityId) {
    qs.set("entity_type", filters.entityType);
    qs.set("entity_id", filters.entityId);
  }
  if (filters?.limit != null) qs.set("limit", String(filters.limit));

  return useQuery({
    queryKey: ["entity-activity", "list", filters],
    queryFn: () =>
      api.get<ListResponse>(`/core/entity-activity?${qs.toString()}`),
    enabled: ready,
  });
}
